import { useFeatureFlags } from '@chronosphereio/core';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { BarGaugeBase } from '@/components/BarGaugeBase/BarGaugeBase';
import { BarChartData, ContentDimensions } from '../../../core';
import { useDataQueryResult } from '../../../plugin-system';
import { GaugeChartPanelProps } from './GaugeChartPanel';
import { DEFAULT_MAX_PERCENT, DEFAULT_UNIT } from './gauge-chart-model';
import { HtmlBarGaugeBase } from './HtmlBarGaugeBase';
import { sortSeriesData } from './utils';

type GaugeChartPanelLoadedProps = Omit<GaugeChartPanelProps, keyof ContentDimensions> & {
  contentDimensions: ContentDimensions;
};

export function BarGaugeChartPanelLoaded(props: GaugeChartPanelLoadedProps) {
  const { max, contentDimensions, thresholds, sort } = props;
  const { devErgoHtmlBarGauge } = useFeatureFlags();

  const unit = useMemo(() => {
    return merge({}, DEFAULT_UNIT, props.unit);
  }, [props.unit]);

  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'SingleValue' });

  const { barChartData, dataMax } = useMemo(() => {
    const barChartData: BarChartData[] = [];
    let dataMax = Number.MIN_SAFE_INTEGER;
    for (const result of queryResults) {
      // Skip queries that are still loading or don't have data
      if (result.isLoading || result.isFetching || result.data === undefined) continue;

      for (const data of result.data.values) {
        const series = {
          value: data.value ?? null,
          label: data.formattedName || data.name,
        };
        if (data.value !== null && data.value > dataMax) {
          dataMax = data.value;
        }
        barChartData.push(series);
      }
    }

    return { barChartData: sortSeriesData(barChartData, sort), dataMax };
  }, [queryResults, sort]);

  let thresholdMax: number;
  if (max === undefined) {
    if (unit.kind === 'Percent') {
      thresholdMax = DEFAULT_MAX_PERCENT;
    } else {
      // If data max is less than one, then we'll assume it's 0-1 percent so we'll set
      // the max to 1.  Otherwise, we will use whatever the max value is as the max
      thresholdMax = dataMax < 1 ? 1 : dataMax;
    }
  } else {
    thresholdMax = max;
  }

  if (devErgoHtmlBarGauge) {
    return (
      <HtmlBarGaugeBase
        width={contentDimensions.width}
        height={contentDimensions.height}
        data={barChartData}
        max={thresholdMax}
        unit={unit}
        thresholds={thresholds}
      />
    );
  }

  return (
    <div
      style={{
        width: contentDimensions.width,
        height: contentDimensions.height,
        overflow: 'auto',
      }}
    >
      <BarGaugeBase
        width={contentDimensions.width}
        height={contentDimensions.height}
        data={barChartData}
        max={thresholdMax}
        unit={unit}
        thresholds={thresholds}
      />
    </div>
  );
}
